import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

/** One demand-override row (history-preserving — includes revoked rows). */
export const DemandOverrideRowSchema = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  productVariantId: z.string().uuid().nullable(),
  periodMonth: z.string(), // YYYY-MM-01
  kind: z.enum(["history", "forecast"]),
  mode: z.enum(["zero", "average", "median", "value"]),
  value: z.number().nullable(),
  reason: z.string().nullable(),
  createdByUserId: z.string().uuid(),
  createdByUserName: z.string().nullable(),
  deactivatedAt: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
});
export type DemandOverrideRow = z.infer<typeof DemandOverrideRowSchema>;

/** Forecast-derived policy levels — the before/after of a history-override preview. */
export const PolicyLevelsSchema = z.object({
  safetyStock: z.number(),
  reorderPoint: z.number(),
  orderUpToLevel: z.number(),
});
export type PolicyLevels = z.infer<typeof PolicyLevelsSchema>;

// A real calendar date (z.iso.date rejects e.g. 2026-99-01) constrained to the
// first of the month — the grain demand overrides are keyed on.
const periodMonth = z.iso.date().refine((v) => v.endsWith("-01"), {
  error: "periodMonth must be a first-of-month YYYY-MM-01.",
});

// ─── List (by product, optionally one warehouse) ─────────────────────────────

export const ListDemandOverridesInputSchema = z.object({
  productId: z.string().uuid(),
  warehouseId: z.string().uuid().optional(),
  /** Include revoked rows. Defaults to true server-side (the table shows history). */
  includeInactive: z.boolean().optional(),
});
export const ListDemandOverridesResponseSchema = z.object({
  overrides: z.array(DemandOverrideRowSchema),
});
export type ListDemandOverridesInput = z.infer<
  typeof ListDemandOverridesInputSchema
>;
export type ListDemandOverridesResponse = z.infer<
  typeof ListDemandOverridesResponseSchema
>;

// ─── Create ──────────────────────────────────────────────────────────────────

export const CreateDemandOverrideInputSchema = z
  .object({
    productId: z.string().uuid(),
    warehouseId: z.string().uuid(),
    periodMonth,
    kind: z.enum(["history", "forecast"]),
    mode: z.enum(["zero", "average", "median", "value"]),
    // Required when mode='value' (and always for kind='forecast'); ignored otherwise.
    value: z.number().nonnegative().nullable().optional(),
    reason: z.string().max(1000).nullable().optional(),
  })
  .refine((v) => v.kind !== "forecast" || v.mode === "value", {
    message: "A forecast override must use mode 'value'.",
    path: ["mode"],
  })
  .refine((v) => v.mode !== "value" || v.value != null, {
    message: "mode 'value' requires a value.",
    path: ["value"],
  });
export const CreateDemandOverrideResponseSchema = z.object({
  override: DemandOverrideRowSchema,
  /** Recomputed levels for a history override (null for forecast pins). */
  recomputed: PolicyLevelsSchema.nullable(),
});
export type CreateDemandOverrideInput = z.infer<
  typeof CreateDemandOverrideInputSchema
>;
export type CreateDemandOverrideResponse = z.infer<
  typeof CreateDemandOverrideResponseSchema
>;

// ─── Preview (history override before/after) ─────────────────────────────────

export const PreviewDemandOverrideInputSchema = z
  .object({
    productId: z.string().uuid(),
    warehouseId: z.string().uuid(),
    periodMonth,
    mode: z.enum(["zero", "average", "median", "value"]),
    value: z.number().nonnegative().nullable().optional(),
  })
  .refine((v) => v.mode !== "value" || v.value != null, {
    message: "mode 'value' requires a value.",
    path: ["value"],
  });
export const PreviewDemandOverrideResponseSchema = z.object({
  before: PolicyLevelsSchema,
  after: PolicyLevelsSchema,
  /** The concrete number the candidate month resolves to (mean/median/value/0). */
  appliedValue: z.number(),
});
export type PreviewDemandOverrideInput = z.infer<
  typeof PreviewDemandOverrideInputSchema
>;
export type PreviewDemandOverrideResponse = z.infer<
  typeof PreviewDemandOverrideResponseSchema
>;

// ─── Revoke ──────────────────────────────────────────────────────────────────

export const RevokeDemandOverrideInputSchema = z.object({
  overrideId: z.string().uuid(),
});
export const RevokeDemandOverrideResponseSchema = z.object({
  override: DemandOverrideRowSchema,
});
export type RevokeDemandOverrideInput = z.infer<
  typeof RevokeDemandOverrideInputSchema
>;
export type RevokeDemandOverrideResponse = z.infer<
  typeof RevokeDemandOverrideResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const demandOverridesContracts = {
  list: oc
    .input(ListDemandOverridesInputSchema)
    .output(ListDemandOverridesResponseSchema)
    .route({
      method: "GET",
      summary:
        "List demand overrides (history + forecast, active + revoked) for a product, optionally scoped to one warehouse.",
    }),
  create: oc
    .input(CreateDemandOverrideInputSchema)
    .output(CreateDemandOverrideResponseSchema)
    .route({
      method: "POST",
      summary:
        "Create a per-month demand override (clean a past month, or pin a future month) and, for history overrides, recompute that series.",
    }),
  preview: oc
    .input(PreviewDemandOverrideInputSchema)
    .output(PreviewDemandOverrideResponseSchema)
    .route({
      method: "GET",
      summary:
        "Preview the before/after safety-stock / reorder-point of a candidate history override without saving it.",
    }),
  revoke: oc
    .input(RevokeDemandOverrideInputSchema)
    .output(RevokeDemandOverrideResponseSchema)
    .route({
      method: "POST",
      summary:
        "Revoke a demand override immediately (sets deactivated_at); recomputes the series for a history override.",
    }),
} as const;
